import { renderHtmlToPlainText } from './renderHtmlToPlainText'
import { escapeHtml, type RenderMode } from './interpolateVariables'

// Rich-text editors emit a new `<p>` per Enter press, but operators expect
// signature rows (name / title / email) on consecutive lines, not blank-line-
// separated paragraphs. Collapse `<p>X</p><p>Y</p>` to `X<br>Y` first.
function normalizeSignatureHtml(html: string): string {
  return html
    .replace(/<\/p>\s*<p[^>]*>/gi, '<br>')
    .replace(/<\/?p[^>]*>/gi, '')
    .replace(/\s+$/, '')
}

// Tag name = letters/digits only, mirroring the server's
// signature_sanitizer.contains_html_tags — an `@` inside the brackets
// (e.g. `Name <email@domain>`) is not a tag.
const SIG_HTML_TAG_RE = /<\/?[a-zA-Z][a-zA-Z0-9]*(?:\s[^>]*)?\/?>/

// Mirrors render.py:plain_text_to_html.
function plainTextToHtml(text: string): string {
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
  return escapeHtml(normalized).replace(/\n/g, '<br>')
}

/**
 * Append signature to body. Byte-parity with the Python `append_signature`.
 *
 * The local canonical signature form is plain text (FIR-7457: HTML is
 * flattened on save), so a tag-less signature is converted here — escaped and
 * `\n` -> `<br>` for HTML mode (raw newlines collapse once Bison sends the
 * body as HTML), passed through verbatim for plain text (an HTML round-trip
 * would swallow `<email@domain>` as a bogus tag). Signatures that still carry
 * HTML (pre-FIR-7457 rows) keep the legacy normalize-and-strip path.
 */
export function appendSignature(
  body: string,
  signatureHtml: string,
  mode: RenderMode
): string {
  if (!signatureHtml) return body
  const isHtmlSig = SIG_HTML_TAG_RE.test(signatureHtml)
  if (mode === 'html') {
    const normalizedSig = isHtmlSig
      ? normalizeSignatureHtml(signatureHtml)
      : plainTextToHtml(signatureHtml.replace(/\s+$/, ''))
    // Separate the signature with one blank line (`<br><br>`), mirroring the
    // plain-text `\n\n` below. Strip any trailing `<br>` the body already has
    // so the gap is exactly one blank line, never two.
    const bodyTrimmed = body.replace(/(?:<br\s*\/?>\s*)+$/i, '')
    return `${bodyTrimmed}<br><br>${normalizedSig}`
  }
  const sigPlain = isHtmlSig
    ? renderHtmlToPlainText(normalizeSignatureHtml(signatureHtml))
    : signatureHtml
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        .replace(/\s+$/, '')
  const bodyTrimmed = body.replace(/\n+$/, '')
  return `${bodyTrimmed}\n\n${sigPlain}`
}
